import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { useRoles } from "@/common/hooks/queries/useRoles";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { RoleData } from "@/common/types/roles";
import { UserData, UserTableItem } from "@/common/types/users";
import { statusOptions } from "@/features/users/constants/statusOptions.constant";
import { useUsers } from "@/features/users/hooks/useUsers";
import { UsersTransformer } from "@/features/users/services/usersTransformer";
import { Search, User } from "lucide-react";
import { defaultColumns } from "./columns";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";

const Users = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [searchRole, setSearchRole] = useDebounce('');

  const {
    data: users,
    meta,
    isPending: usersIsPending,
    isLoading: usersIsLoading,
    error: usersError = {} as any,
    refetch: refetchUsers,
  } = useUsers({
    filters: {
      page: filterParams.page || 1,
      search,
      role: filterParams.role?.value,
      status: filterParams.status?.value
    },
    mapper: Mapper.mapWithTransformer<UserData[], UserTableItem[]>({
      transformer: UsersTransformer.tableTransformer()
    })
  });

  const { data: rolesOptions } = useRoles({
    filters: { search: searchRole },
    mapper: Mapper.mapToOptions<RoleData>({ labelFieldName: 'display_name', valueFieldName: 'id' })
  });

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<User className="w-10 h-10" strokeWidth={1.5} />}
        title="Usuários Tcard"
        createButtonText="Cadastrar"
        dataTestid="create_user_button"
        disableCreateButton={usersIsPending || usersError?.status === 403}
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={usersError?.status}
        refetchFunction={refetchUsers}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<Search />}
              name="search"
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
              }}
              value={filterParams.search}
            />

            <ListFilters
              filters={[
                {
                  className: 'w-[160px]',
                  onChange: (role) => setFilterParams((prev) => ({ ...prev, role })),
                  onInputChange: setSearchRole,
                  value: filterParams.role,
                  options: [{ label: 'Todos', value: '' }, ...(rolesOptions || [])],
                  placeholder: 'Perfil'
                },
                {
                  className: 'w-[128px]',
                  onChange: (status) => setFilterParams((prev) => ({ ...prev, status })),
                  value: filterParams.status,
                  options: [{ label: 'Todos', value: '' }, ...(statusOptions || [])],
                  placeholder: 'Status'
                },
              ]}
            />
          </div>

          <Table
            columns={defaultColumns}
            isLoadingData={usersIsLoading}
            page={filterParams.page || 1}
            rows={users}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            rowEmptyValue="--"
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default Users;
